import React, { CSSProperties, PropsWithChildren } from 'react';
import { useOrientation } from 'hooks/useOrientation';
import { useSupportsHover } from 'hooks/useSupportsHover';

// Locks its subtree to a PORTRAIT layout on mobile, even when the device is held
// in landscape: it renders a portrait-shaped box (short side = viewport height,
// long side = viewport width) and rotates it 90° to fill the landscape screen.
// Because the wrapper carries a `transform`, it becomes the containing block for
// the lobby's fixed/absolute descendants, so the whole lobby adopts portrait.
//
// Desktop/web (hover-capable pointer) and actual portrait are untouched — the
// wrapper collapses to `display: contents`, adding no box at all.
type ForcePortraitProps = PropsWithChildren<{
  // Extra gate ANDed with the mobile+landscape check. Defaults to true (the lobby
  // always locks). App-level overlays pass `enabled={activeView === 'lobby'}` so
  // they only rotate while the portrait-locked lobby is showing — not over a
  // landscape table.
  enabled?: boolean;
  // z-index of the rotated layer. The lobby base uses the default (1); overlays
  // (cashier, etc.) pass a higher value so they stack above the lobby.
  zIndex?: number;
  // When true the rotated layer itself ignores pointer events, so an always-
  // mounted overlay (e.g. CashierPanel, which slides off-screen rather than
  // unmounting) doesn't blanket the lobby with an invisible click-blocker while
  // closed. The overlay's own interactive surfaces must re-enable pointer events
  // (pointer-events is inherited). The lobby base leaves this off — it IS the
  // interactive surface.
  transparentToPointer?: boolean;
}>;

const ForcePortrait: React.FC<ForcePortraitProps> = ({
  children,
  enabled = true,
  zIndex = 1,
  transparentToPointer = false
}) => {
  const supportsHover = useSupportsHover();
  const orientation = useOrientation();
  const lockToPortrait = enabled && !supportsHover && orientation === 'landscape';

  const lockedStyle: CSSProperties = {
    ...rotatedStyle,
    zIndex,
    ...(transparentToPointer ? { pointerEvents: 'none' } : {})
  };

  return (
    <div data-testid='force-portrait' style={lockToPortrait ? lockedStyle : passthroughStyle}>
      {children}
    </div>
  );
};

// No box — the subtree lays out exactly as if this wrapper weren't here.
const passthroughStyle: CSSProperties = { display: 'contents' };

// Portrait box rotated to fill the landscape viewport. The transform
// `rotate(90deg) translate(0, -100%)` (origin top-left) maps the rotated box
// onto [0..100vw] × [0..100vh], i.e. the full screen.
const rotatedStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vh',
  height: '100vw',
  transform: 'rotate(90deg) translate(0, -100%)',
  transformOrigin: 'top left',
  overflow: 'hidden',
  zIndex: 1
};

export default ForcePortrait;
